import React, { useState } from "react"
import { ReactSVG } from "react-svg"

import { graphql, Link } from "gatsby"
import {
  DatoCmsAllLocalesForDatoCmsArticleSlug,
  DatoCmsSlugsConfiguration,
  HeaderContactFieldsFragment,
  HeaderLegalMentionsFieldsFragment,
  HeaderNotFoundFieldsFragment,
  HeaderOurApproachFieldsFragment,
  HeaderPniFieldsFragment,
} from "../../../../graphql-types"

import * as styles from "./header-generic.module.scss"
import { changeLanguage } from "../../common/functions/functions"
import SearchModule from "../../common/jsx/search-module/search-module"
import { useLocale } from "../../../context/locale-context"
import useSlugValue from "../../../hooks/use-slug-value"
import useWindowWidth from "../../../hooks/use-window-width"
import backArrow from "../../../../static/vectors/backArrow.svg"
import downArrow from "../../../../static/vectors/downArrow.svg"
import cross from "../../../../static/vectors/cross.svg"
import lensIcon from "../../../../static/vectors/lensIcon.svg"

type HeaderGenericProps = {
  data:
    | HeaderPniFieldsFragment
    | HeaderOurApproachFieldsFragment
    | HeaderContactFieldsFragment
    | HeaderLegalMentionsFieldsFragment
    | HeaderNotFoundFieldsFragment
  slugs: DatoCmsSlugsConfiguration
  allSlugLocales: [DatoCmsAllLocalesForDatoCmsArticleSlug]
  breadcrumb?: { link: string; title: string }[]
  currentRoute: string
  darkMode?: boolean
}

const HeaderGeneric: React.FC<HeaderGenericProps> = ({
  data,
  slugs,
  allSlugLocales,
  breadcrumb,
  currentRoute,
  darkMode,
}) => {
  const { locale, defaultLocale } = useLocale()
  const isIntermediateSize = useWindowWidth() <= 1100
  const isMobile = useWindowWidth() <= 825
  const [searchModuleOpened, setSearchModuleOpened] = useState(false)
  const [searchHover, setSearchHover] = useState(false)
  const [searchSelect, setSearchSelect] = useState(false)

  const contactSlug = useSlugValue(slugs._allContactPageSlugLocales, locale)
  const pniSlug = useSlugValue(slugs._allProjectsNewsInsightsPageSlugLocales, locale)
  const searchRequestSlug = locale === defaultLocale ? `${pniSlug}` : `${locale}/${pniSlug}`

  const onSearchMouseOver = () => {
    setSearchHover(true)
    setTimeout(() => setSearchHover(true), 400)
  }
  const onSearchMouseLeave = () => {
    if (searchSelect) return
    setTimeout(() => setSearchHover(false), 400)
  }
  const onSearchSelect = () => setSearchSelect(true)
  const onSearchBlur = () => {
    setSearchSelect(false)
    setTimeout(() => setSearchHover(false), 400)
  }

  const trimLongSlugName = (slug: string): string => {
    if (isIntermediateSize) {
      if (slug.length > 20) {
        return slug.slice(0, 20) + "..."
      }
    } else {
      if (slug.length > 45) {
        return searchHover ? slug.slice(0, 20) + "..." : slug.slice(0, 45) + "..."
      }
    }
    return slug
  }

  if (searchModuleOpened) {
    return (
      <div className={styles.menuContainer}>
        <div className={styles.searchModuleContent}>
          <section className={styles.iconContainer}>
            <button className={styles.iconCircle} onClick={() => setSearchModuleOpened(false)} aria-label="Close menu">
              <ReactSVG src={cross} className={styles.icon} />
            </button>
          </section>
          <section className={styles.searchBarContainer}>
            <SearchModule
              searchPlaceholder=""
              searchRequestSlug={searchRequestSlug}
              isMobileSearchBar={true}
            />
          </section>
        </div>
      </div>
    )
  }

  return (
    <header className={darkMode ? styles.headerDark : styles.header} role="banner">
      <div className={styles.headerContent}>
        <div className={styles.breadcrumb}>
          <Link
            to={defaultLocale === locale ? `/` : `/${locale}`}
            className={darkMode ? styles.homeBtnDark : styles.homeBtn}
          >
            <ReactSVG className={styles.homeBtnSvg} src={backArrow} />
            <span className={styles.homeBtnText}>{data.headerHome}</span>
          </Link>

          {breadcrumb?.map((br, index) => {
            return (
              <Link
                key={index}
                to={locale === defaultLocale ? `/${br.link}` : `/${locale}/${br.link}`}
                className={darkMode ? styles.linkBtnDark : styles.linkBtn}
                tabIndex={0}
              >
                <ReactSVG className={styles.linkBtnSvg} src={downArrow} />
                <span className={styles.homeBtnText}>{trimLongSlugName(br.title)}</span>
              </Link>
            )
          })}
        </div>
        <div className={styles.websiteSettingsAndSearch}>
          {isMobile && !searchModuleOpened ? (
            <button
              className={darkMode ? styles.iconCircleDark : styles.iconCircle}
              onClick={() => setSearchModuleOpened(false)}
              aria-label="Search icon"
            >
              <ReactSVG src={lensIcon} className={styles.icon} />
            </button>
          ) : (
            <SearchModule
              searchPlaceholder=""
              searchRequestSlug={searchRequestSlug}
              darkMode={darkMode}
              onMouseOver={onSearchMouseOver}
              onMouseLeave={onSearchMouseLeave}
              onSelect={onSearchSelect}
              onBlur={onSearchBlur}
            />
          )}
          <Link
            to={locale === defaultLocale ? `/${contactSlug}` : `/${locale}/${contactSlug}`}
            className={darkMode ? styles.contactButtonDark : styles.contactButton}
          >
            {data.headerSayHello}
          </Link>
          <ul className={darkMode ? styles.langSelectorDark : styles.langSelector}>
            <li>
              <Link
                to={changeLanguage(currentRoute, slugs, allSlugLocales, "en", defaultLocale)}
                title="EN English version"
              >
                <span style={{ fontWeight: locale === "en" ? "bold" : "normal" }}>EN</span>
              </Link>
            </li>
            <li>
              <Link
                to={changeLanguage(currentRoute, slugs, allSlugLocales, "fr", defaultLocale)}
                title="FR version française"
              >
                <span style={{ fontWeight: locale === "fr" ? "bold" : "normal" }}>FR</span>
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </header>
  )
}

export const fragmentPNI = graphql`
  fragment HeaderPNIFields on DatoCmsPniPage {
    headerTitle
    headerHome
    headerSayHello
  }
`

export const fragmentOurApproach = graphql`
  fragment HeaderOurApproachFields on DatoCmsOurApproachPage {
    headerTitle
    headerHome
    headerSayHello
  }
`

export const fragmentContact = graphql`
  fragment HeaderContactFields on DatoCmsContactPage {
    headerTitle
    headerHome
    headerSayHello
  }
`

export const fragmentLegalMentions = graphql`
  fragment HeaderLegalMentionsFields on DatoCmsLegalMentionsPage {
    headerTitle
    headerHome
    headerSayHello
  }
`

export const fragmentConfidentialityPolicy = graphql`
  fragment HeaderConfidentialityPolicyFields on DatoCmsConfidentialityPolicyPage {
    headerTitle
    headerHome
    headerSayHello
  }
`

export const fragmentCookies = graphql`
  fragment HeaderCookiesFields on DatoCmsCookiesPage {
    headerTitle
    headerHome
    headerSayHello
  }
`

export const fragmentCredits = graphql`
  fragment HeaderCreditsFields on DatoCmsCreditsPage {
    headerTitle
    headerHome
    headerSayHello
  }
`

export const fragmentNotFound = graphql`
  fragment HeaderNotFoundFields on DatoCmsNotFoundPage {
    title
    headerHome
    headerSayHello
  }
`

export default HeaderGeneric
